import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import {
  faUndo,
  faSearchMinus,
  faSearchPlus,
  faCompress,
  faExpand,
  faHistory,
  faArrowLeft,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';

import { TranslateModule } from '@ngx-translate/core';

import { Entity } from './models/entity.model';
import { ViewerCanvasService } from './services/viewer-canvas.service';

@Component({
  selector: 'app-viewer',
  standalone: true,
  imports: [FontAwesomeModule, TranslateModule],
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.scss'],
})
export class ViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input('images') imagesArray: string[] = [];
  @Input() labels: string[] = [];
  @Input() imgIndex: number = 0;
  @Input() numOfPages: number = 0;
  @Input() loading: boolean = false;
  @Input() reset?: Observable<void>;
  @Input() entities: Entity[] = [];

  @Output() changeImgIndex: EventEmitter<number> = new EventEmitter<number>();
  @Output() entityClick: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('viewer') viewer!: ElementRef;
  @ViewChild('image') image!: ElementRef;
  @ViewChild('canvas') canvas!: ElementRef;

  public get currentPage() {
    return this.imgIndex + 1;
  }

  imgShowing: SafeResourceUrl | null = null;
  imgTranslate: string = '';
  imgScale: string = '';
  imgRotate: string = '';
  currentImgLabel: string = '';
  isFullScreen = false;

  icons = {
    faUndo,
    faSearchMinus,
    faSearchPlus,
    faCompress,
    faExpand,
    faHistory,
    faArrowLeft,
    faArrowRight,
  };

  private imgTranslateValue: number[] = [0, 0];
  private imgScaleValue = 1;
  private imgRotateValue = 0;
  private imgDrag: any = {
    dragging: false,
    position: [0, 0],
    newPosition: [0, 0],
  };

  private subscriptions = new Subscription();

  constructor(
    private renderer: Renderer2,
    private domSanitizer: DomSanitizer,
    private canvasService: ViewerCanvasService
  ) {}

  ngOnInit(): void {
    this.imgTranslate = 'translate(0px, 0px)';
    this.imgScale = 'scale(1)';
    this.imgRotate = 'rotate(-0deg)';
    this.subscriptions.add(
      this.reset?.subscribe(() => {
        this.resetViewer();
      })
    );
  }

  ngAfterViewInit() {
    this.setScrollListener();
    this.initializeCanvas();
  }

  private initializeCanvas() {
    if (this.canvas && this.image && this.viewer) {
      const canvasElement = this.canvas.nativeElement as HTMLCanvasElement;
      const imageElement = this.image.nativeElement as HTMLImageElement;
      const viewerElement = this.viewer.nativeElement as HTMLElement;

      this.canvasService.initialize(
        canvasElement,
        imageElement,
        viewerElement,
        (entityId: number) => this.onEntityClick(entityId)
      );

      const handleImageLoad = () => {
        if (this.entities && this.entities.length > 0) {
          this.canvasService.setEntities(this.entities, this.currentPage);
        }
      };

      if (imageElement.complete && imageElement.naturalWidth > 0) {
        handleImageLoad();
      } else {
        imageElement.onload = handleImageLoad;
      }
    }
  }

  private updateCanvas() {
    if (this.entities && this.entities.length > 0) {
      this.canvasService.setEntities(this.entities, this.currentPage);
    }
  }

  private onEntityClick(entityId: number) {
    this.entityClick.emit(entityId);
    this.canvasService.setActiveEntity(entityId);
  }

  setScrollListener() {
    this.renderer.listen(this.viewer.nativeElement, 'wheel', (e) =>
      this.onMousewheel(e)
    );
  }

  resetViewer() {
    this.imgIndex = 0;
    this.updateCurrentImage();
    this.notifyImgIndexChange();
  }

  ngOnChanges(c: SimpleChanges) {
    if (c['imgIndex'] && !c['imgIndex'].firstChange) {
      this.changeToPage(c['imgIndex'].currentValue);
    } else if (c['imagesArray']) {
      this.updateCurrentImage();
    }

    if (c['entities'] && this.canvas) {
      this.updateCanvas();
    }
  }

  updateCurrentImage() {
    this.currentImgLabel = this.labels[this.imgIndex] || '';
    const url = (this.imagesArray?.length && this.imagesArray[this.imgIndex]) || '';
    if (url) {
      this.imgShowing = this.domSanitizer.bypassSecurityTrustResourceUrl(url);
    } else {
      this.imgShowing = null;
    }
  }


  nextPage() {
    const maxPage = this.numOfPages > 0 ? this.numOfPages - 1 : this.imagesArray.length - 1;
    
    if (this.imgIndex < maxPage) {
      this.imgIndex++;
      this.updateCurrentImage();
      this.notifyImgIndexChange();
      this.canvasService.setPage(this.currentPage);
    }
  }

  prevPage() {
    if (this.imgIndex > 0) {
      this.imgIndex--;
      this.updateCurrentImage();
      this.notifyImgIndexChange();
      this.canvasService.setPage(this.currentPage);
    }
  }

  notifyImgIndexChange() {
    this.changeImgIndex.emit(this.imgIndex);
  }

  changeToPage(i: number): void {
    if (i >= 0 && i < this.imagesArray.length) {
      this.imgIndex = i;
      this.updateCurrentImage();
      this.notifyImgIndexChange();
      this.canvasService.setPage(this.currentPage);
    }
  }

  changeScale(v: number): void {
    this.imgScaleValue = v;
    this.imgScale = `scale(${v})`;
    this.canvasService.setImageTransform(
      v,
      this.imgTranslateValue as [number, number],
      this.imgRotateValue
    );
  }

  changeTranslation(v: number[]): void {
    this.imgTranslateValue = v;
    this.imgTranslate = `translate(${v[0]}px, ${v[1]}px)`;
    this.canvasService.setImageTransform(
      this.imgScaleValue,
      v as [number, number],
      this.imgRotateValue
    );
  }

  changeRotation(v: number): void {
    this.imgRotateValue = v;
    this.imgRotate = `rotate(-${v}deg)`;
    this.canvasService.setImageTransform(
      this.imgScaleValue,
      this.imgTranslateValue as [number, number],
      v
    );
  }

  @HostListener('mousedown', ['$event'])
  onDragDown(e: MouseEvent) {
    this.imgDrag.dragging = true;
    this.imgDrag.position[0] = e.clientX;
    this.imgDrag.position[1] = e.clientY;
  }

  @HostListener('mousemove', ['$event'])
  onDragMove(e: MouseEvent) {
    e.preventDefault();
    if (this.imgDrag.dragging) {
      this.imgDrag.newPosition[0] = this.imgDrag.position[0] - e.clientX;
      this.imgDrag.newPosition[1] = this.imgDrag.position[1] - e.clientY;
      this.imgDrag.position[0] = e.clientX;
      this.imgDrag.position[1] = e.clientY;
      const newPosition = [
        this.imgTranslateValue[0] -
          this.imgDrag.newPosition[0] / this.imgScaleValue,
        this.imgTranslateValue[1] -
          this.imgDrag.newPosition[1] / this.imgScaleValue,
      ];
      this.changeTranslation(newPosition);
    }
  }

  @HostListener('mouseup')
  @HostListener('mouseout')
  onDragUp() {
    this.imgDrag.dragging = false;
  }

  pass(mode: string): void {
    if (mode === 'next') {
      this.imagesArray.length > this.imgIndex + 1 ? this.imgIndex++ : null;
    } else {
      this.imgIndex > 0 ? this.imgIndex-- : null;
    }

    this.changeToPage(this.imgIndex);
  }

  onZoom(mode: string): void {
    if (mode === 'in') {
      if (this.imgScaleValue < 1) {
        this.imgScaleValue = 1;
      } else {
        this.imgScaleValue === 1
          ? (this.imgScaleValue += 0.3)
          : (this.imgScaleValue = 1 + (this.imgScaleValue - 1) * 1.8);
      }
    } else {
      this.imgScaleValue < 1
        ? (this.imgScaleValue = 0.8)
        : (this.imgScaleValue = this.imgScaleValue * 0.8);
    }

    this.changeScale(this.imgScaleValue);
  }

  onMousewheel(event: WheelEvent) {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.onZoom('in');
    }
    if (event.deltaY > 0) {
      this.onZoom('out');
    }
  }

  onTranslate(mode: string): void {
    if (mode === 'left') {
      this.imgTranslateValue[0] += 30;
    } else if (mode === 'right') {
      this.imgTranslateValue[0] -= 30;
    } else if (mode === 'down') {
      this.imgTranslateValue[1] -= 50;
    } else {
      this.imgTranslateValue[1] += 50;
    }

    this.changeTranslation(this.imgTranslateValue);
  }

  onRotate(): void {
    if (this.imgRotateValue >= 270) {
      this.imgRotateValue = 0;
    } else {
      this.imgRotateValue += 90;
    }

    this.changeRotation(this.imgRotateValue);
  }

  onFullScreen(): void {
    this.isFullScreen = !this.isFullScreen;
  }

  onReset(): void {
    this.changeTranslation([0, 0]);
    this.changeRotation(0);
    this.changeScale(1);
  }

  resetView(): void {
    this.canvasService.setActiveEntity(null);
    this.onReset();
  }

  onInput(e: any): void {
    const file = e.target.files[0];
    this.bs64(file);
  }

  bs64(src: File): void {
    const reader = new FileReader();
    reader.readAsDataURL(src);
    reader.onload = () => {
      this.imagesArray.push(reader.result as string);
      if (this.imagesArray.length === 1) {
        this.imgShowing = this.imagesArray[0];
      }
    };
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
    this.canvasService.destroy();
  }

  focusOnEntity(entityId: number): void {
    const focusData = this.canvasService.focusOnEntity(entityId);
    if (focusData) {
      if (focusData.page !== undefined && focusData.page !== this.currentPage) {
        this.changeToPage(focusData.page - 1);
        setTimeout(() => {
          const updatedFocusData = this.canvasService.focusOnEntity(entityId);
          if (updatedFocusData) {
            this.changeScale(updatedFocusData.scale);
            this.changeTranslation([updatedFocusData.translate[0], updatedFocusData.translate[1]]);
          }
          this.canvasService.setActiveEntity(entityId);
        }, 100);
      } else {
        this.changeScale(focusData.scale);
        this.changeTranslation([focusData.translate[0], focusData.translate[1]]);
        this.canvasService.setActiveEntity(entityId);
      }
    }
  }
}
